import React, { useEffect, useMemo, useReducer } from 'react'
import { IntlProvider, RawIntlProvider, createIntl, createIntlCache } from 'react-intl'
import './styles.css'
import { useBrand } from 'contexts/BrandContext'
import { rebrandMessages, rebrandText } from 'brands/rebrand'
import Routes from 'containers/Routes'
import Player from 'containers/Player'
import ErrorBoundary from 'components/ErrorBoundary'
import { translationMessages, defaultLocale } from 'i18n'
import 'icons'
import { ProjectContextProvider } from 'contexts/ProjectContext'
import TagManager from 'react-gtm-module'
import { GlobalStyles } from 'twin.macro'
import { CurrentUserContextProvider } from 'hooks/useCurrentUser'
import { MediaPlayerProvider } from 'contexts/PlayerContext'
import { DndProvider } from 'react-dnd'
import { HTML5Backend } from 'react-dnd-html5-backend'
import { useLocation } from 'react-router-dom'
import NiceModal from '@ebay/nice-modal-react'

const intlCache = createIntlCache()

// A message missing from the translation table falls back to the defaultMessage in the
// component's descriptor, which rebrandMessages() never sees - so rewrite that too.
const BrandedIntlProvider = ({ brand, locale, messages, children }) => {
  const intl = useMemo(() => {
    const base = createIntl({ locale, messages, defaultLocale }, intlCache)
    const formatMessage = (descriptor, ...rest) =>
      base.formatMessage(
        descriptor?.defaultMessage
          ? { ...descriptor, defaultMessage: rebrandText(brand, descriptor.defaultMessage) }
          : descriptor,
        ...rest
      )

    return { ...base, formatMessage, $t: formatMessage }
  }, [brand, locale, messages])

  return <RawIntlProvider value={intl}>{children}</RawIntlProvider>
}

export const IntlContext = React.createContext({
  locale: defaultLocale,
  messages: [],
})

const localStorage =
  typeof window !== 'undefined'
    ? window.localStorage
    : {
        getItem() {
          return null
        },
      }

// VoiceArchive's copy is the original, so it keeps the plain provider.
const BrandIntlProvider = ({ brand, locale, messages, children }) =>
  brand.rewrites.length ? (
    <BrandedIntlProvider brand={brand} locale={locale} messages={messages}>
      {children}
    </BrandedIntlProvider>
  ) : (
    <IntlProvider locale={locale} messages={messages} defaultLocale={defaultLocale}>
      {children}
    </IntlProvider>
  )

const App = ({ locale, ...rest }) => {
  const location = useLocation()
  const brand = useBrand()
  // A host brand's own name, links and addresses replace VoiceArchive's in the copy; a
  // project's message overwrites (ProjectContext) still win over both.
  const brandedMessages = (messageLocale) =>
    rebrandMessages(brand, translationMessages[messageLocale])
  const initialLocale = localStorage?.getItem('App.locale') || locale || defaultLocale
  const [intl, dispatch] = useReducer(
    (state, { action, payload }) => {
      if (action == 'SET_LOCALE') {
        localStorage?.setItem('App.locale', payload)

        return {
          ...state,
          locale: payload,
          messages: {
            ...brandedMessages(payload),
            ...state.overwrites,
          },
        }
      }

      if (action == 'OVERWRITE') {
        return {
          ...state,
          messages: { ...state.messages, ...payload },
          overwrites: payload,
        }
      }

      return state
    },
    {
      locale: initialLocale,
      messages: brandedMessages(initialLocale),
      overwrites: {},
    }
  )

  // The theme is not applied here: the server renders the host brand's theme, and
  // ProjectContext owns it on the client (a project's brand wins over the host's).
  useEffect(() => {
    if (brand.gtmId) {
      TagManager.initialize({ gtmId: brand.gtmId })
    }
  }, [])

  return (
    <IntlContext.Provider value={{ intl, dispatch }}>
      <BrandIntlProvider brand={brand} locale={intl.locale} messages={intl.messages}>
        <ErrorBoundary>
          <NiceModal.Provider>
            <ProjectContextProvider>
              <MediaPlayerProvider>
                <CurrentUserContextProvider>
                  <DndProvider backend={HTML5Backend}>
                    <GlobalStyles />
                    <Routes />
                    <Player />
                  </DndProvider>
                </CurrentUserContextProvider>
              </MediaPlayerProvider>
            </ProjectContextProvider>
          </NiceModal.Provider>
        </ErrorBoundary>
      </BrandIntlProvider>
    </IntlContext.Provider>
  )
}

export default App
